import { useEffect, useState, useMemo } from "react";
import { Phone, MapPin, Sparkles } from "lucide-react";

const LAUNCH_DATE = new Date("2026-06-30T23:59:59+02:00");

function useCountdown(target) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    return useMemo(() => {
        const diff = Math.max(0, target.getTime() - now.getTime());
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);
        return { days, hours, minutes, seconds, done: diff === 0 };
    }, [now, target]);
}

const pad = (n) => String(n).padStart(2, "0");

const CountUnit = ({ value, label, testid }) => (
    <div
        className="count-card rounded-2xl px-4 py-5 sm:px-6 sm:py-7 flex flex-col items-center justify-center min-w-[78px] sm:min-w-[110px]"
        data-testid={testid}
    >
        <div className="count-num text-5xl sm:text-6xl md:text-7xl tracking-tight">
            {pad(value)}
        </div>
        <div className="count-label mt-3">{label}</div>
    </div>
);

export default function ComingSoon() {
    const { days, hours, minutes, seconds } = useCountdown(LAUNCH_DATE);

    return (
        <main
            className="grain-bg relative min-h-screen w-full overflow-hidden"
            data-testid="coming-soon-page"
        >
            {/* Soft floating ornament */}
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full float-slow"
                style={{
                    background:
                        "radial-gradient(circle, rgba(200,164,92,0.25), transparent 70%)",
                    filter: "blur(20px)",
                }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-40 -left-32 h-[520px] w-[520px] rounded-full float-slow"
                style={{
                    background:
                        "radial-gradient(circle, rgba(168,133,61,0.18), transparent 70%)",
                    filter: "blur(20px)",
                    animationDelay: "2s",
                }}
            />

            <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col px-6 py-10 sm:px-10 sm:py-14">
                {/* Top bar */}
                <header
                    className="flex items-center justify-between fade-up"
                    data-testid="top-bar"
                >
                    <div
                        className="flex items-center gap-2 text-[11px] uppercase tracking-[0.32em]"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        <span
                            className="inline-block h-1.5 w-1.5 rounded-full dot-pulse"
                            style={{ background: "var(--gold-deep)" }}
                        />
                        <span>Physiotherapie · Osteopathie</span>
                    </div>
                    <div
                        className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.32em]"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        <Sparkles
                            className="h-3.5 w-3.5"
                            style={{ color: "var(--gold-deep)" }}
                        />
                        <span>Go live 2026</span>
                    </div>
                </header>

                {/* Center content */}
                <section
                    className="flex flex-1 flex-col items-center justify-center text-center pt-8 sm:pt-4"
                    data-testid="hero-section"
                >
                    {/* Logo */}
                    <div
                        className="w-full max-w-[640px] fade-up"
                        style={{ animationDelay: "0.05s" }}
                        data-testid="logo-wrap"
                    >
                        <img
                            src="/logo.png"
                            alt="RUF – Die Knochenhelden"
                            className="w-full h-auto select-none"
                            draggable={false}
                            data-testid="logo-image"
                        />
                    </div>

                    {/* Headline */}
                    <h1
                        className="font-serif-display mt-10 sm:mt-12 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl fade-up"
                        style={{ animationDelay: "0.35s", color: "var(--ink)" }}
                        data-testid="headline"
                    >
                        Wir öffnen hier in Kürze unsere
                        <span
                            className="italic"
                            style={{ color: "var(--gold-deep)" }}
                        >
                            {" "}
                            virtuellen Praxistüren.
                        </span>
                    </h1>

                    {/* Subhead */}
                    <p
                        className="mt-6 max-w-xl text-base sm:text-lg fade-up"
                        style={{
                            color: "var(--ink-soft)",
                            animationDelay: "0.45s",
                        }}
                        data-testid="subhead"
                    >
                        Physiotherapie und Osteopathie auf höchstem Niveau —
                        mit Hand, Herz und Sachverstand.
                    </p>

                    {/* Hairline */}
                    <div
                        className="mt-10 w-32 hair-line draw-line"
                        aria-hidden
                    />

                    {/* Countdown */}
                    <div
                        className="mt-10 grid grid-cols-4 gap-3 sm:gap-5 fade-up"
                        style={{ animationDelay: "0.55s" }}
                        data-testid="countdown"
                    >
                        <CountUnit
                            value={days}
                            label="Tage"
                            testid="count-days"
                        />
                        <CountUnit
                            value={hours}
                            label="Stunden"
                            testid="count-hours"
                        />
                        <CountUnit
                            value={minutes}
                            label="Minuten"
                            testid="count-minutes"
                        />
                        <CountUnit
                            value={seconds}
                            label="Sekunden"
                            testid="count-seconds"
                        />
                    </div>

                    <p
                        className="mt-6 text-xs sm:text-sm fade-up"
                        style={{
                            color: "var(--ink-soft)",
                            animationDelay: "0.7s",
                        }}
                        data-testid="launch-date"
                    >
                        Go live &middot; 30. Juni 2026 · 23:59 Uhr
                    </p>
                </section>

                {/* Contact footer */}
                <footer
                    className="mt-14 sm:mt-20 pt-8 fade-up"
                    style={{
                        borderTop: "1px solid var(--line)",
                        animationDelay: "0.85s",
                    }}
                    data-testid="contact-footer"
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 items-start">
                        <div data-testid="contact-name">
                            <div className="count-label mb-2">Praxis</div>
                            <div
                                className="font-serif-display text-xl sm:text-2xl"
                                style={{ color: "var(--ink)" }}
                            >
                                RUF — Die Knochenhelden
                            </div>
                            <div
                                className="mt-1 text-sm"
                                style={{ color: "var(--ink-soft)" }}
                                data-testid="contact-owner"
                            >
                                Inhaber: Thilo Ruf
                            </div>
                        </div>

                        <a
                            href="tel:+493068322927"
                            className="group flex items-start gap-3 transition-opacity hover:opacity-80"
                            data-testid="contact-phone"
                        >
                            <span
                                className="mt-1 flex h-9 w-9 items-center justify-center rounded-full"
                                style={{
                                    background:
                                        "rgba(200,164,92,0.15)",
                                    color: "var(--gold-deep)",
                                }}
                            >
                                <Phone className="h-4 w-4" />
                            </span>
                            <span>
                                <div className="count-label mb-1">
                                    Terminanfrage
                                </div>
                                <div
                                    className="font-serif-display text-lg sm:text-xl"
                                    style={{ color: "var(--ink)" }}
                                >
                                    030 68322927
                                </div>
                            </span>
                        </a>

                        <a
                            href="https://maps.google.com/?q=Alt+Zepernick+8,+16341+Panketal"
                            target="_blank"
                            rel="noreferrer"
                            className="group flex items-start gap-3 transition-opacity hover:opacity-80"
                            data-testid="contact-address"
                        >
                            <span
                                className="mt-1 flex h-9 w-9 items-center justify-center rounded-full"
                                style={{
                                    background:
                                        "rgba(200,164,92,0.15)",
                                    color: "var(--gold-deep)",
                                }}
                            >
                                <MapPin className="h-4 w-4" />
                            </span>
                            <span>
                                <div className="count-label mb-1">
                                    Standort
                                </div>
                                <div
                                    className="font-serif-display text-lg sm:text-xl leading-tight"
                                    style={{ color: "var(--ink)" }}
                                >
                                    Alt Zepernick 8
                                    <br />
                                    16341 Panketal
                                </div>
                            </span>
                        </a>
                    </div>

                    <div
                        className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[11px] uppercase tracking-[0.3em]"
                        style={{ color: "var(--ink-soft)" }}
                        data-testid="legal-row"
                    >
                        <span>
                            © {new Date().getFullYear()} RUF — Die Knochenhelden
                        </span>
                        <span className="opacity-70">
                            Mit Sorgfalt in Vorbereitung
                        </span>
                    </div>
                </footer>
            </div>
        </main>
    );
}
